import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Berhasil masuk");
      nav("/", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Gagal login");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center bg-background overflow-hidden">
      {/* Ambient background */}
      <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" />
      <div
        aria-hidden
        className="absolute -top-32 -left-40 h-96 w-96 rounded-full bg-primary/10 blur-3xl pointer-events-none"
      />
      <div
        aria-hidden
        className="absolute -bottom-40 -right-32 h-[28rem] w-[28rem] rounded-full bg-foreground/5 blur-3xl pointer-events-none"
      />

      {/* Card */}
      <div className="relative z-10 w-full max-w-md px-6">
        <div className="mb-10 flex flex-col items-center text-center">
          <div className="h-14 w-14 rounded-md bg-foreground text-background flex items-center justify-center font-black text-2xl shadow-lg shadow-foreground/10">
            P
          </div>
          <div className="mt-4 label-mini">Presales · Collab</div>
        </div>

        <form
          onSubmit={submit}
          className="card-tech p-8 space-y-6 shadow-xl shadow-foreground/[0.04]"
          data-testid="login-form"
        >
          <div className="space-y-1.5">
            <h2 className="text-2xl font-heading font-black tracking-tight">Selamat datang</h2>
            <p className="text-sm text-muted-foreground">Masuk untuk melanjutkan.</p>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="label-mini">Email</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="label-mini">Password</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-11"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            data-testid="login-submit-button"
            className="w-full h-11 rounded-sm font-semibold group"
          >
            {loading ? "Memproses..." : (
              <>
                Masuk
                <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </>
            )}
          </Button>
        </form>

        <div className="mt-6 text-center text-[11px] tracking-widest uppercase font-bold text-muted-foreground/70">
          © {new Date().getFullYear()} Presales Collab
        </div>
      </div>
    </div>
  );
}
